import { Component, DestroyRef, effect, inject, input, linkedSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BrnSelectImports } from '@spartan-ng/brain/select';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSelectImports } from '@spartan-ng/helm/select';
import { toast } from 'ngx-sonner';
import { v7 as uuidv7 } from 'uuid';

import { LabelRequiredDirective } from '@shared/directives/label-required.directive';
import { SidepanelService } from '../../../../shared/components/sidepanel/sidepanel.service';
import { ToastrSuccessComponent } from '../../../../shared/components/toastr/toastr-success.component';
import { Livrable } from '../models/livrable.model';
import { LivrableUpdateRequest } from '../requests/livrvable-update-request';
import { FamilleLivrablesStore } from '../store/famille-livrables.store';
import { LivrableStore } from '../store/livrable.store';
import { Référentiel } from '../../../../administration/gestion-référentiels/models/référentiel.model';

@Component({
  selector: 'app-form-livrable',
  imports: [
    ReactiveFormsModule,
    HlmFieldImports,
    HlmSelectImports,
    BrnSelectImports,
    HlmInputImports,
    LabelRequiredDirective,
  ],
  templateUrl: './form-livrable.component.html',
})
export class FormLivrableComponent {
  readonly #fb = inject(FormBuilder);
  readonly #sidepanelService = inject(SidepanelService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #familleLivrablesStore = inject(FamilleLivrablesStore);
  readonly #livrableStore = inject(LivrableStore);
  readonly #familleStore = inject(FamilleLivrablesStore);

  readonly livrable = input.required<Livrable>();
  readonly referentiel = input.required<Référentiel>();

  readonly familles = linkedSignal(() => this.#familleStore.entities());

  readonly form = this.#fb.nonNullable.group({
    idFamilleLivrables: [null],
    nom: [null, Validators.required],
  });

  constructor() {
    effect(() => {
      const livrable = this.livrable();

      this.idFamilleLivrable.setValue(livrable.idFamilleLivrables);
      this.nom.setValue(livrable.nom);
    });

    this.#sidepanelService.submit.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => this.onSubmit());
  }

  onSubmit() {
    if (this.nom.hasError('required')) {
      return;
    }

    const toastrId = uuidv7();

    const livrable: LivrableUpdateRequest = {
      idFamilleLivrables: this.idFamilleLivrable.value,
      nom: this.nom.value,
    };

    this.#livrableStore.modifier({
      idLivrable: this.livrable().id,
      data: livrable,
      next: () => {
        toast.custom(ToastrSuccessComponent, {
          componentProps: {
            message: 'Modification du livrable réussie',
            id: toastrId,
          },
          id: toastrId,
        });
        this.#familleLivrablesStore.charger({
          idCartographie: this.referentiel().idCartographie,
          idRéférentiel: this.referentiel().id,
        });
        this.#sidepanelService.close();
      },
      error: () => this.alreadyExists(livrable),
    });
  }

  alreadyExists(livrable: LivrableUpdateRequest) {
    const livrablesDeLaFamille = this.familles()
      .filter((x) => x.id === livrable.idFamilleLivrables)
      .flatMap((x) => x.livrables)
      .map((x) => x?.nom);
    const doublonsAvecLivrablesExistants = livrablesDeLaFamille.filter((x) => livrable.nom === x);

    if (doublonsAvecLivrablesExistants.length > 0) {
      this.nom.setErrors({ notUnique: true });
    }
  }

  get idFamilleLivrable() {
    return this.form.get('idFamilleLivrables') as AbstractControl;
  }

  get nom() {
    return this.form.get('nom') as AbstractControl;
  }
}
